import React, {useEffect, useState, useMemo} from "react";
import {Box, Typography} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NavBar from "../pages/NavBar/NavBar";
import {getIdMeBaseApiUrl} from "../../utils/UrlConstants";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import InputText from "../common/InputText";
import Button from "../common/Button";
import Dropdown from "../../utils/components/Dropdown";
import { toast } from "react-toastify";
import FooterData from "../pages/NavBar/FooterData";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { useIdentityAppService } from "../../services/IdentityServices";

// Interface for organization from API
interface OrganizationOption {
    orgCd: string;
    orgName: string;
    orgType: {
        id: number;
        codeType: string;
        code: string;
        value: string;
        createdDate: string;
        modifiedDate: string;
        createdBy: string;
        modifiedBy: string;
        description: string;
    };
    stateIssuedId?: string;
    addresses?: OrganizationAddress[];
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T & { cancel: () => void } {
    let timeoutId: NodeJS.Timeout;

    const debouncedFunction = ((...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    }) as T & { cancel: () => void };

    debouncedFunction.cancel = () => {
        clearTimeout(timeoutId);
    };

    return debouncedFunction;
}

// Interface for organization domain
interface OrganizationDomain {
    orgDomainId: number;
    domainName: string;
    isPrimary: boolean;
    isVerified: boolean;
    operationalFlag: string;
    operationalStatus: string;
}

// Interface for organization address
interface OrganizationAddress {
    orgAddressId: number;
    addressType?: {
        id: number;
        codeType: string;
        code: string;
        value: string;
    };
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state?: {
        id: number;
        codeType: string;
        code: string;
        value: string;
    };
    zip: string;
    country?: {
        id: number;
        codeType: string;
        code: string;
        value: string;
    };
    isPrimary: boolean;
    operationalFlag: string;
    operationalStatus: string;
}

interface UserDto {
    userId: number;
    userLogin: string;
    userType: string;
    userEmail: string;
    userPhone: string;
    title?: string;
    userIdpGuid: string;
    firstName: string;
    lastName: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
    userStatus: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    organizationName?: string;
    organizationType?: string;
    stateIssuedId?: string;
    organizationAddressId?: number;
}

const AdminEditAccount: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const identityAppService = useIdentityAppService();

    const initialUser: UserDto = location.state?.user || null;
    const returnPath = location.state?.returnPath || '/admin-dashboard';

    const [currentUser, setCurrentUser] = useState<UserDto | null>(initialUser);
    const [editedInfo, setEditedInfo] = useState<any>({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        title: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        userType: '',
        organizationName: '',
        organizationType: '',
        stateIssuedId: '',
    });
    const [errors, setErrors] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [notifyUser, setNotifyUser] = useState(false);
    const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
    const [countryOptions, setCountryOptions] = useState<{ value: string; label: string }[]>([]);
    const [userTypeOptions, setUserTypeOptions] = useState<{ value: string; label: string }[]>([]);
    const [organizationTypeOptions, setOrganizationTypeOptions] = useState<{ value: string; label: string }[]>([]);

    // Organization search state
    const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
    const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
    const [orgSearchLoading, setOrgSearchLoading] = useState(false);

    // Organization address state
    const [organizationAddresses, setOrganizationAddresses] = useState<OrganizationAddress[]>([]);
    const [selectedOrgAddressId, setSelectedOrgAddressId] = useState<number | null>(null);

    // Address suggestion state
    const [suggestedAddress, setSuggestedAddress] = useState<{
        addressLine1: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
        country: string;
    }>({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: ''
    });

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }

        if (!currentUser) {
            toast.error('No user data found. Redirecting to Admin Dashboard.');
            navigate('/admin-dashboard');
            return;
        }

        // Initialize edited info with user data
        // Note: userType and organizationType will be mapped in a separate useEffect after options are loaded
        setEditedInfo({
            username: currentUser.userLogin || '',
            firstName: currentUser.firstName || '',
            lastName: currentUser.lastName || '',
            email: currentUser.userEmail || '',
            phone: currentUser.userPhone || '',
            title: currentUser.title || '',
            addressLine1: currentUser.addressLine1 || '',
            addressLine2: currentUser.addressLine2 || '',
            city: currentUser.city || '',
            state: currentUser.state || '',
            zip: currentUser.zip || '',
            country: currentUser.country || '',
            userType: currentUser.userType || '',
            organizationName: currentUser.organizationName || '',
            organizationType: currentUser.organizationType || '',
            stateIssuedId: currentUser.stateIssuedId || '',
        });

        // Initialize selected organization address if present
        if (currentUser.organizationAddressId) {
            setSelectedOrgAddressId(currentUser.organizationAddressId);
        }
    }, [currentUser, navigate]);

    // Effect to fetch state codes
    useEffect(() => {
        const fetchStateCodes = async () => {
            try {
                const response = await useIdentityAppService().getCodeLookupByType();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.codes) {
                        const states = data.codes.map((code: any) => ({
                            value: code.code,
                            label: code.displayLabel || `${code.code} - ${code.value}`
                        }));
                        setStateOptions(states);
                    }
                }
            } catch (error) {
                console.error('Error fetching state codes:', error);
            }
        };

        fetchStateCodes();
    }, []);

    // Effect to fetch country codes
    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                const response = await useIdentityAppService().getCodeLookupByTypeCountryDropdown();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.codes) {
                        const countries = data.codes.map((code: any) => ({
                            value: code.code,
                            label: code.displayLabel || `${code.code} - ${code.value}`
                        }));
                        setCountryOptions(countries);

                        // Default to USA if country is not already set
                        setEditedInfo((prev: any) => {
                            if (!prev.country || prev.country.trim() === '') {
                                const usaCountry = countries.find((c: any) => c.value === 'US' || c.value === 'USA');
                                return {
                                    ...prev,
                                    country: usaCountry ? usaCountry.value : ''
                                };
                            }
                            return prev;
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching country codes:', error);
            }
        };

        fetchCountryCodes();
    }, []);

    // Effect to fetch user types
    useEffect(() => {
        const fetchUserTypes = async () => {
            try {
                const response = await useIdentityAppService().getUserTypes();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.userTypes && Array.isArray(data.userTypes)) {
                        const types = data.userTypes.map((type: any) => ({
                            value: type.value,
                            label: type.label
                        }));
                        setUserTypeOptions(types);
                    }
                }
            } catch (error) {
                console.error('Error fetching user types:', error);
            }
        };

        fetchUserTypes();
    }, []);

    // Effect to fetch organization types
    useEffect(() => {
        const fetchOrganizationTypes = async () => {
            try {
                const response = await useIdentityAppService().getOrganizationTypes();

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.organizationTypes && Array.isArray(data.organizationTypes)) {
                        const types = data.organizationTypes.map((type: any) => ({
                            value: type.value,
                            label: type.value  // Use value for both since API doesn't have label property
                        }));
                        setOrganizationTypeOptions(types);
                    }
                }
            } catch (error) {
                console.error('Error fetching organization types:', error);
            }
        };

        fetchOrganizationTypes();
    }, []);

    // Effect to map userType and organizationType after options are loaded
    useEffect(() => {
        if (currentUser && userTypeOptions.length > 0 && organizationTypeOptions.length > 0) {
            setEditedInfo((prev: any) => {
                const updates: any = {};

                // Map userType: Backend returns VALUE (e.g., "Citizen"), but dropdown expects CODE (e.g., "10")
                if (currentUser.userType) {
                    const userTypeMatch = userTypeOptions.find(
                        (option) => option.label.toLowerCase() === currentUser.userType.toLowerCase()
                    );
                    if (userTypeMatch) {
                        updates.userType = userTypeMatch.value;
                    }
                }

                // Map organizationType: Backend returns CODE, but dropdown expects VALUE (full name)
                if (currentUser.organizationType) {
                    const orgTypeMatch = organizationTypeOptions.find(
                        (option) => option.value === currentUser.organizationType ||
                                   option.label === currentUser.organizationType
                    );
                    if (orgTypeMatch) {
                        updates.organizationType = orgTypeMatch.value;
                    }
                }

                return { ...prev, ...updates };
            });
        }
    }, [currentUser, userTypeOptions, organizationTypeOptions]);

    // Effect to preload organization details if user already has an organization
    useEffect(() => {
        const preloadOrganization = async () => {
            // Only preload if user has an organization name and we haven't loaded it yet
            if (currentUser?.organizationName && organizationOptions.length === 0) {
                try {
                    setOrgSearchLoading(true);
                    const response = await useIdentityAppService().searchOrganizations(currentUser.organizationName);

                    if (response.ok) {
                        const data = await response.json();
                        if (data.success && data.organizations && data.organizations.length > 0) {
                            setOrganizationOptions(data.organizations);

                            // Extract organization details from the first matching organization
                            const organization = data.organizations[0];

                            // Update organizationType with the correct value from the fetched organization
                            // This ensures the dropdown pre-populates correctly since the backend may store
                            // the code but the dropdown uses the display value
                            if (organization.orgType?.value) {
                                setEditedInfo((prev: any) => ({
                                    ...prev,
                                    organizationType: organization.orgType.value
                                }));
                            }

                            if (organization.addresses && organization.addresses.length > 0) {
                                const activeAddresses = organization.addresses.filter(
                                    (address: OrganizationAddress) => address.operationalStatus === 'Active'
                                );
                                setOrganizationAddresses(activeAddresses);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error preloading organization:', error);
                } finally {
                    setOrgSearchLoading(false);
                }
            }
        };

        preloadOrganization();
    }, [currentUser]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        // Special handling for phone field - format as (XXX)-XXX-XXXX
        if (name === 'phone') {
            const formattedPhone = formatPhoneNumber(value);
            setEditedInfo((prev: any) => ({
                ...prev,
                phone: formattedPhone
            }));
        } else {
            setEditedInfo((prev: any) => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev: any) => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;

        // If organization type is changing, clear the organization name and state issued ID
        if (name === 'organizationType') {
            setEditedInfo((prev: any) => ({
                ...prev,
                organizationType: value,
                organizationName: '',
                stateIssuedId: ''
            }));
            setOrganizationOptions([]); // Clear existing options
            setIsOrgDropdownOpen(false);
        } else {
            setEditedInfo((prev: any) => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when user selects an option
        if (errors[name]) {
            setErrors((prev: any) => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Function to search organizations from API
    const searchOrganizations = async (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 2) {
            setOrganizationOptions([]);
            setIsOrgDropdownOpen(false);
            return;
        }

        try {
            setOrgSearchLoading(true);
            const response = await useIdentityAppService().searchOrganizations(searchTerm);

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.organizations) {
                    setOrganizationOptions(data.organizations);
                    setIsOrgDropdownOpen(data.organizations.length > 0);
                } else {
                    setOrganizationOptions([]);
                    setIsOrgDropdownOpen(false);
                }
            } else {
                console.error('Failed to search organizations:', response.status);
                setOrganizationOptions([]);
                setIsOrgDropdownOpen(false);
            }
        } catch (error: any) {
            console.error('Error searching organizations:', error);
            setOrganizationOptions([]);
            setIsOrgDropdownOpen(false);
        } finally {
            setOrgSearchLoading(false);
        }
    };

    // Debounced organization search
    const debouncedSearchOrganizations = useMemo(
        () => debounce(searchOrganizations, 300),
        []
    );

    // Handle organization input changes with search
    const handleOrganizationInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setEditedInfo((prev: any) => ({
            ...prev,
            [name]: value
        }));

        // If it's the organization name field, trigger search
        if (name === 'organizationName') {
            if (value.trim().length >= 2) {
                debouncedSearchOrganizations(value.trim());
            } else {
                setOrganizationOptions([]);
                setIsOrgDropdownOpen(false);
            }
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev: any) => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Handle organization selection from dropdown/search
    const handleOrganizationSelection = (organization: OrganizationOption) => {
        // Extract active addresses from the organization
        const activeAddresses = organization.addresses?.filter(
            (address: OrganizationAddress) => address.operationalStatus === 'Active'
        ) || [];

        // Find primary address or use first active address
        const primaryAddress = activeAddresses.find(addr => addr.isPrimary) || activeAddresses[0];

        setEditedInfo((prev: any) => ({
            ...prev,
            organizationName: organization.orgName,
            organizationType: organization.orgType.value,
            stateIssuedId: organization.stateIssuedId || '',
            // Populate address fields if primary/first address exists
            addressLine1: primaryAddress?.addressLine1 || '',
            addressLine2: primaryAddress?.addressLine2 || '',
            city: primaryAddress?.city || '',
            state: primaryAddress?.state?.code || '',
            zip: primaryAddress?.zip || '',
            country: primaryAddress?.country?.code || ''
        }));

        // Set organization addresses for display
        setOrganizationAddresses(activeAddresses);

        // Auto-select the primary address if available
        if (primaryAddress) {
            setSelectedOrgAddressId(primaryAddress.orgAddressId);
        } else {
            setSelectedOrgAddressId(null);
        }

        setIsOrgDropdownOpen(false);
        setOrganizationOptions([]);
    };

    // Handle address suggestion - for now, returns the same address
    // This will be replaced with API call later
    const handleSuggestAddress = () => {
        // For now, just use the current address as the "suggested" address
        // Later, this will call an API to validate/suggest the address
        const currentAddress = {
            addressLine1: editedInfo.addressLine1 || '',
            addressLine2: editedInfo.addressLine2 || '',
            city: editedInfo.city || '',
            state: editedInfo.state || '',
            zip: editedInfo.zip || '',
            country: editedInfo.country || ''
        };

        setSuggestedAddress(currentAddress);

        // Apply the suggested address to the form
        setEditedInfo((prev: any) => ({
            ...prev,
            ...currentAddress
        }));
    };

    // Function to fetch fresh user data from server
    const fetchUserData = async (username: string) => {
        try {
            const response = await identityAppService.getUserByUsername(username);

            if (response.status === 200 && response.data) {
                setCurrentUser(response.data);
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching updated user data:', error);
            toast.error('Could not refresh user data. Please refresh the page.');
        }
        return null;
    };

    const validateForm = () => {
        const newErrors: any = {};

        if (!editedInfo.firstName?.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!editedInfo.lastName?.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!editedInfo.email?.trim()) {
            newErrors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(editedInfo.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        if (!editedInfo.phone?.trim()) {
            newErrors.phone = 'Phone number is required';
        } else {
            const phoneRegex = /^[\d\s\-\(\)\+]+$/;
            if (!phoneRegex.test(editedInfo.phone)) {
                newErrors.phone = 'Please enter a valid phone number';
            }
        }

        // Address validation - if any address field is filled, all required fields must be filled
        const addressFieldsFilled = editedInfo.addressLine1?.trim() || editedInfo.city?.trim() ||
                                  editedInfo.state?.trim() || editedInfo.zip?.trim() || editedInfo.country?.trim();

        if (addressFieldsFilled) {
            if (!editedInfo.addressLine1?.trim()) {
                newErrors.addressLine1 = 'Address Line 1 is required when providing address';
            }
            if (!editedInfo.city?.trim()) {
                newErrors.city = 'City is required when providing address';
            }
            if (!editedInfo.state?.trim()) {
                newErrors.state = 'State is required when providing address';
            }
            if (!editedInfo.zip?.trim()) {
                newErrors.zip = 'ZIP code is required when providing address';
            }
            if (!editedInfo.country?.trim()) {
                newErrors.country = 'Country is required when providing address';
            }
        }

        // ZIP code format validation
        if (editedInfo.zip?.trim()) {
            const zipRegex = /^\d{5}(-\d{4})?$/;
            if (!zipRegex.test(editedInfo.zip)) {
                newErrors.zip = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
            }
        }

        // User type validation
        if (!editedInfo.userType?.trim()) {
            newErrors.userType = 'User type is required';
        }

        // Organization validation - required if user type is Trading Partner (code "30")
        if (editedInfo.userType === '30') {
            if (!editedInfo.organizationName?.trim()) {
                newErrors.organizationName = 'Organization name is required for Trading Partner';
            }
            if (!editedInfo.organizationType?.trim()) {
                newErrors.organizationType = 'Organization type is required for Trading Partner';
            }
            if (!editedInfo.stateIssuedId?.trim()) {
                newErrors.stateIssuedId = 'State issued ID is required for Trading Partner';
            }
        }

        return newErrors;
    };

    const handleSave = async () => {
        const newErrors = validateForm();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setLoading(true);
            // Base64 encode the email before sending to API
            const encodedEmail = btoa(editedInfo.email);
            const updateData = {
                username: editedInfo.username,
                firstName: editedInfo.firstName,
                lastName: editedInfo.lastName,
                email: encodedEmail,
                phone: editedInfo.phone.replace(/\D/g, ''), // Strip formatting, send only digits
                jobTitle: editedInfo.title,
                addressLine1: editedInfo.addressLine1,
                addressLine2: editedInfo.addressLine2,
                city: editedInfo.city,
                state: editedInfo.state,
                zip: editedInfo.zip,
                country: editedInfo.country,
                userType: editedInfo.userType,
                organizationName: editedInfo.organizationName,
                organizationType: editedInfo.organizationType,
                stateIssuedId: editedInfo.stateIssuedId,
                selectedOrgAddressId: selectedOrgAddressId,
                notifyUser: notifyUser
            };

            const response = await identityAppService.updateUser(updateData);

            if (response.status === 200) {
                // Check if email notification was requested and its status
                if (notifyUser) {
                    if (response.data.emailSent) {
                        toast.success('User information updated successfully! Notification email sent.');
                    } else if (response.data.emailError) {
                        toast.warning('User information updated but notification email failed to send.');
                    } else {
                        toast.success('User information updated successfully!');
                    }
                } else {
                    toast.success('User information updated successfully!');
                }

                // Reset notify user checkbox after successful save
                setNotifyUser(false);

                // Fetch fresh user data from server to reflect the updates
                await fetchUserData(editedInfo.username);
            }
        } catch (error) {
            console.error('Error updating user information:', error);
            toast.error('Failed to update user information. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/manage-user', { state: { user: currentUser } });
    };

    if (!currentUser) {
        return (
            <React.Fragment>
                <NavBar/>
                <Box sx={{ padding: '20px', textAlign: 'center' }}>
                    <Typography>No user data found. Redirecting...</Typography>
                </Box>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <NavBar/>
            <Box sx={{width: '100%', minHeight: '100vh', backgroundColor: '#f5f5f5'}}>
                <div style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    margin: '0 auto',
                    maxWidth: '800px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}>
                    <Typography variant="h4" sx={{ marginBottom: '30px', color: '#00607f', textAlign: 'center', fontWeight: 'bold' }}>
                        Edit User Account - {currentUser.firstName} {currentUser.lastName}
                    </Typography>

                    {/* Personal Information */}
                    <Typography variant="h6" sx={{ marginBottom: '20px', color: '#00607f', fontWeight: 'bold' }}>
                        Personal Information
                    </Typography>

                    <Box sx={{ marginBottom: '20px' }}>
                        <InputText
                            label="Username"
                            name="username"
                            value={editedInfo.username || ''}
                            type="text"
                            onChange={() => {}} // No-op since it's read-only
                            mandatoryLabel={false}
                            readOnly={true}
                            placeholder="Username"
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <Box>
                            <InputText
                                label="First Name"
                                name="firstName"
                                value={editedInfo.firstName || ''}
                                type="text"
                                onChange={handleInputChange}
                                mandatoryLabel={true}
                                placeholder="Enter first name"
                            />
                            {errors.firstName && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.firstName}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <InputText
                                label="Last Name"
                                name="lastName"
                                value={editedInfo.lastName || ''}
                                type="text"
                                onChange={handleInputChange}
                                mandatoryLabel={true}
                                placeholder="Enter last name"
                            />
                            {errors.lastName && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.lastName}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <Box>
                            <InputText
                                label="Email"
                                name="email"
                                value={editedInfo.email || ''}
                                type="email"
                                onChange={handleInputChange}
                                mandatoryLabel={true}
                                placeholder="Enter email address"
                            />
                            {errors.email && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.email}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <InputText
                                label="Phone"
                                name="phone"
                                value={editedInfo.phone || ''}
                                type="tel"
                                onChange={handleInputChange}
                                mandatoryLabel={true}
                                placeholder="Enter phone number"
                            />
                            {errors.phone && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.phone}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ marginBottom: '30px' }}>
                        <InputText
                            label="Job Title"
                            name="title"
                            value={editedInfo.title || ''}
                            type="text"
                            onChange={handleInputChange}
                            mandatoryLabel={false}
                            placeholder="Enter job title"
                        />
                        {errors.jobTitle && (
                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                {errors.jobTitle}
                            </Typography>
                        )}
                    </Box>

                    {/* User Type Section */}
                    <Typography variant="h6" sx={{ marginTop: '30px', marginBottom: '20px', color: '#00607f', fontWeight: 'bold' }}>
                        User Type
                    </Typography>

                    <Box sx={{ marginBottom: '30px' }}>
                        <Dropdown
                            key={`user-type-${editedInfo.userType}`}
                            choices={userTypeOptions}
                            emptyChoiceLabel="Select User Type"
                            mandatoryLabel={true}
                            name="userType"
                            label="User Type"
                            value={editedInfo.userType || ''}
                            onChange={handleDropdownChange}
                            disabled={true}
                        />
                        <Typography sx={{ fontSize: '12px', color: '#666', marginTop: '5px', fontStyle: 'italic' }}>
                            User Type cannot be changed after account creation
                        </Typography>
                        {errors.userType && (
                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                {errors.userType}
                            </Typography>
                        )}
                    </Box>

                    {/* Organization Details - Only show for Trading Partner (userType === "30") */}
                    {editedInfo.userType === '30' && (
                        <>
                            <Typography variant="h6" sx={{ marginTop: '30px', marginBottom: '20px', color: '#00607f', fontWeight: 'bold' }}>
                                Organization Information
                            </Typography>

                            <Box sx={{ marginBottom: '20px' }}>
                                <Dropdown
                                    key={`org-type-${editedInfo.organizationType}`}
                                    choices={organizationTypeOptions}
                                    emptyChoiceLabel="Select Organization Type"
                                    mandatoryLabel={true}
                                    name="organizationType"
                                    label="Organization Type"
                                    value={editedInfo.organizationType || ''}
                                    onChange={handleDropdownChange}
                                />
                                {errors.organizationType && (
                                    <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                        {errors.organizationType}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ marginBottom: '20px', position: 'relative' }}>
                                <InputText
                                    label="Organization Name"
                                    name="organizationName"
                                    value={editedInfo.organizationName || ''}
                                    type="text"
                                    onChange={handleOrganizationInputChange}
                                    mandatoryLabel={true}
                                    placeholder="Start typing organization name..."
                                />

                                {/* Loading indicator */}
                                {orgSearchLoading && (
                                    <Typography sx={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '35px',
                                        fontSize: '12px',
                                        color: '#666'
                                    }}>
                                        Searching...
                                    </Typography>
                                )}

                                {/* Organization dropdown */}
                                {isOrgDropdownOpen && organizationOptions.length > 0 && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        zIndex: 1000,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}>
                                        {organizationOptions.map((org) => (
                                            <Box
                                                key={org.orgCd}
                                                onClick={() => handleOrganizationSelection(org)}
                                                sx={{
                                                    padding: '10px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #eee',
                                                    backgroundColor: 'white',
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5'
                                                    }
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                                                    {org.orgName}
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                                    Type: {org.orgType.value}
                                                    {org.stateIssuedId && (
                                                        <span> | ID: {org.stateIssuedId}</span>
                                                    )}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {errors.organizationName && (
                                    <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                        {errors.organizationName}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ marginBottom: '30px' }}>
                                <InputText
                                    label="State Issued ID"
                                    name="stateIssuedId"
                                    value={editedInfo.stateIssuedId || ''}
                                    type="text"
                                    onChange={handleInputChange}
                                    mandatoryLabel={true}
                                    placeholder="Enter state issued ID"
                                />
                                {errors.stateIssuedId && (
                                    <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                        {errors.stateIssuedId}
                                    </Typography>
                                )}
                            </Box>

                            {/* Organization Addresses Section */}
                            {organizationAddresses.length > 0 && (
                                <Box sx={{ marginBottom: '30px', marginTop: '30px' }}>
                                    <Typography variant="h6" sx={{ marginBottom: '15px', color: '#00607f', fontWeight: 'bold' }}>
                                        Organization Addresses
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#666', marginBottom: '15px', fontStyle: 'italic' }}>
                                        Select an address to associate with this user account
                                    </Typography>

                                    <Box sx={{
                                        padding: '15px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        backgroundColor: '#f9f9f9'
                                    }}>
                                        {organizationAddresses.map((address, index) => (
                                            <Box
                                                key={address.orgAddressId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: '10px',
                                                    padding: '12px',
                                                    marginBottom: '10px',
                                                    backgroundColor: selectedOrgAddressId === address.orgAddressId ? '#d4edda' : '#e3f2fd',
                                                    border: selectedOrgAddressId === address.orgAddressId ? '2px solid #28a745' : '1px solid #00607f',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedOrgAddressId === address.orgAddressId}
                                                    onChange={() => {
                                                        const isDeselecting = selectedOrgAddressId === address.orgAddressId;
                                                        setSelectedOrgAddressId(isDeselecting ? null : address.orgAddressId);

                                                        // Populate or clear Address Information fields
                                                        if (isDeselecting) {
                                                            // Clear address fields when deselecting
                                                            setEditedInfo((prev: any) => ({
                                                                ...prev,
                                                                addressLine1: '',
                                                                addressLine2: '',
                                                                city: '',
                                                                state: '',
                                                                zip: '',
                                                                country: ''
                                                            }));
                                                        } else {
                                                            // Populate address fields with selected organization address
                                                            setEditedInfo((prev: any) => ({
                                                                ...prev,
                                                                addressLine1: address.addressLine1 || '',
                                                                addressLine2: address.addressLine2 || '',
                                                                city: address.city || '',
                                                                state: address.state?.code || '',
                                                                zip: address.zip || '',
                                                                country: address.country?.code || ''
                                                            }));
                                                        }
                                                    }}
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        cursor: 'pointer',
                                                        marginTop: '2px'
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                                        Address {index + 1}
                                                        {selectedOrgAddressId === address.orgAddressId && (
                                                            <Typography component="span" sx={{
                                                                marginLeft: '10px',
                                                                color: '#28a745',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                ✓ Selected for User
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    <Typography>{address.addressLine1}</Typography>
                                                    {address.addressLine2 && <Typography>{address.addressLine2}</Typography>}
                                                    <Typography>
                                                        {address.city}, {address.state?.code || ''} {address.zip}
                                                    </Typography>
                                                    <Typography>{address.country?.code || ''}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}

                    {/* Address Information */}
                    <Typography variant="h6" sx={{ marginTop: '30px', marginBottom: '15px', color: '#00607f', fontWeight: 'bold' }}>
                        Address Information
                        <Typography component="span" sx={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                            (All fields except Address Line 2 are required)
                        </Typography>
                    </Typography>

                    <Box sx={{ marginBottom: '20px' }}>
                        <InputText
                            label="Address Line 1"
                            name="addressLine1"
                            value={editedInfo.addressLine1 || ''}
                            type="text"
                            onChange={handleInputChange}
                            mandatoryLabel={false}
                            placeholder="Enter street address"
                        />

                        {/* Address suggestion helper text and button */}
                        {editedInfo.addressLine1?.trim() && (
                            <Box sx={{
                                marginTop: '5px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: '#666',
                                    flex: '1'
                                }}>
                                    Click to validate and use suggested address
                                </Typography>

                                <button
                                    type="button"
                                    onClick={handleSuggestAddress}
                                    style={{
                                        fontSize: '12px',
                                        padding: '4px 8px',
                                        backgroundColor: '#00607f',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = '#004d66';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = '#00607f';
                                    }}
                                >
                                    Use Suggested
                                </button>
                            </Box>
                        )}

                        {errors.addressLine1 && (
                            <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                {errors.addressLine1}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ marginBottom: '20px' }}>
                        <InputText
                            label="Address Line 2"
                            name="addressLine2"
                            value={editedInfo.addressLine2 || ''}
                            type="text"
                            onChange={handleInputChange}
                            mandatoryLabel={false}
                            placeholder="Apartment, suite, unit, etc. (optional)"
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <Box>
                            <InputText
                                label="City"
                                name="city"
                                value={editedInfo.city || ''}
                                type="text"
                                onChange={handleInputChange}
                                mandatoryLabel={false}
                                placeholder="Enter city"
                            />
                            {errors.city && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.city}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Dropdown
                                choices={stateOptions}
                                emptyChoiceLabel="Select State"
                                mandatoryLabel={false}
                                name="state"
                                label="State"
                                value={editedInfo.state || ''}
                                onChange={handleDropdownChange}
                            />
                            {errors.state && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.state}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <Box>
                            <InputText
                                label="ZIP Code"
                                name="zip"
                                value={editedInfo.zip || ''}
                                type="text"
                                onChange={handleInputChange}
                                mandatoryLabel={false}
                                placeholder="12345 or 12345-6789"
                            />
                            {errors.zip && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.zip}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Dropdown
                                choices={countryOptions}
                                emptyChoiceLabel="Select Country"
                                mandatoryLabel={false}
                                name="country"
                                label="Country"
                                value={editedInfo.country || ''}
                                onChange={handleDropdownChange}
                            />
                            {errors.country && (
                                <Typography sx={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                                    {errors.country}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Notify User Checkbox */}
                    <Box sx={{
                        marginTop: '30px',
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: '#f0f7ff',
                        borderRadius: '8px',
                        border: '1px solid #cce5ff'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            gap: '10px'
                        }}>
                            <input
                                type="checkbox"
                                checked={notifyUser}
                                onChange={(e) => setNotifyUser(e.target.checked)}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer'
                                }}
                            />
                            <Box>
                                <Typography sx={{ fontWeight: 'bold', color: '#00607f' }}>
                                    Notify User
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                    Send an email notification to the user about the profile changes
                                </Typography>
                            </Box>
                        </label>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                        <Button onClick={handleSave} disabled={loading}>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button onClick={handleCancel}>
                            Cancel
                        </Button>
                    </Box>
                </div>
            </Box>
            <FooterData />
        </React.Fragment>
    );
};

export default AdminEditAccount;